import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

// Custom components
import TableLoto from "../../../components/atoms/tables/TableLoto";
import { ShowDialog } from "../../../components/ShowDialog";
import { BackdropLoading } from "../../../components/BackdropLoading";

// Styles
import { Container } from "../../styles";
import { PageTitle } from "../../Workflow/styles";

// Icons
import { useQrScanner } from "../../../Hooks/useQrScanner";
import { QrScannerModal } from "../../../components/molecules/modals/QrScannerModal";

// Utils

// Offline functionality
import { useOffline } from "../../../Context/OfflineContext";
import { useNavigate, useParams } from "react-router-dom";
import Router from "../../../router";

import { useTranslation } from "react-i18next";
import { Grid, Icon } from "@mui/material";
import { SearchBar } from './../../../components/atoms/searchBars/SearchBar/index';

export const LotoList = (props) => {
  const { t } = useTranslation();
  const { open } = props;
  const navigate = useNavigate();
  const params = useParams();
  const workflowId = params?.id || "";
  
  // Offline functionality
  const {
    connectionStatus,
    getOfflineForm,
    getOfflineSidebar
  } = useOffline();


  const [data, setData] = useState([]);
  const [workflow, setWorkflow] = useState({});
  const [rowsCount, setRowsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredRequest, setFilteredRequest] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [dialog, setDialog] = useState({
    status: false,
    title: "",
    message: "",
    type: null,
  });
  const [headers, setHeaders] = useState([]);

  // QR Scanner Hook
  const {
    openQrModal,
    startScanning,
    handleOpenQrScan,
    handleCloseQrModal,
    handleStartScanning,
    handleQrScan,
    handleQrError
  } = useQrScanner();

  useEffect(() => {
    getWorkflowData();
    returnHeaders();

    async function call() {
      loadData();
    }
    call();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    
    // Si estamos offline, cargar datos directamente desde localStorage
    if (!connectionStatus) {
      
      // Obtener todos los formularios descargados desde localStorage
      const savedForms = localStorage.getItem('cenit-offline-forms');
      
      if (!savedForms) {
        setData([]);
        setFilteredRequest([]);
        setRowsCount(0);
        setIsLoading(false);
        return;
      }
      
      const parsedForms = JSON.parse(savedForms);
      const formIds = Object.keys(parsedForms);
      // Construir array de formularios con sus datos
      const offlineResults = formIds.map(formId => {
        const formData = parsedForms[formId].data;
        return {
          id: formId,
          code: formData.code || formId,
          formId: formData.formId,
          workflowId: formData.workflowId,
          workflowStatus: formData.workflowStatus,
          plant: formData.plant || { name: 'N/A' },
          updatedAt: formData.updatedAt,
          ...formData
        };
      });

      // Ordenar por updatedAt descendente (más reciente primero)
      offlineResults.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });

      setData(offlineResults);
      setFilteredRequest(offlineResults);
      setRowsCount(offlineResults.length);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(false);
  };

  const getWorkflowData = () => {
    const sidebar = getOfflineSidebar();
    if (sidebar?.length > 0 && workflowId) {
      const currentWorkflow = sidebar.find((el) => el.id.toString() === workflowId.toString());
      setWorkflow(currentWorkflow || {});
    }
  }

  const returnHeaders = () => {
    let heads = [
      {
        id: "code",
        label: "workflow.table.id",
        center: false,
        disablePadding: true,
        sortArrow: true,
        isRender: true,
        link: true,
        sticky: true,
        direction: "left",
      },
      {
        id: "workflowStatus",
        label: "workflow.table.status",
        center: false,
        disablePadding: true,
        sortArrow: true,
        isRender: true,
        type: "workflowStatus"
      },
      {
        id: "plant",
        label: "workflow.table.plant",
        center: false,
        disablePadding: true,
        sortArrow: true,
        isRender: true,
      },
      {
        id: "updatedAt",
        label: "workflow.table.lastUpdate",
        center: false,
        disablePadding: true,
        sortArrow: true,
        type: "date",
      },
      {
        id: "syncStatus",
        label: "lotoWorklow.table.syncStatus",
        center: true,
        disablePadding: true,
        sortArrow: true,
      },
      {
        id: "actions",
        label: "common.actions.title",
        center: true,
        disablePadding: false,
        sortArrow: false,
        type: "actions",
        sticky: true,
        direction: "right",
        keys: ["process"],
        customActions: true,
      },
    ];

    setHeaders(heads);
  };

  // Función para procesar el formulario (con QR)
  const handleProcessForm = (row) => {
    // Abrir modal con escáner QR via hook
    handleOpenQrScan(row);
  };

  const handleScanSuccess = (data) => {
    const { row } = data;
    if (row?.id && row?.workflowId) {
      const route = Router.appOfflineDynamicForm.replace(":id", row.id);
      navigate(route, {
        state: {
          workflowId: row.workflowId,
          formId: row.formId
        }
      });
      setSelectedRow(row);
      handleCloseQrModal();
    }
  };

  // Redirigir a detalle de la instancia
  const handleViewFormDetail = (row) => {
    const storedWorkflow = getOfflineForm(row.id);

    if (storedWorkflow) {
      const route = Router.appOfflineWorkflowDetail
        .replace(":workflowId", storedWorkflow.workflow.workflowId)
        .replace(":id", row.id);
      navigate(route);
    }
  };

  const validateQrLotoList = async (qrResult, row) => {
    try {
      // Se obtiene el detalle desde localStorage
      const storedWorkflow = getOfflineForm(row.id);
      const parentPreviousData = storedWorkflow?.formData?.parentPreviousData;
      
      if (!parentPreviousData) {
        toast.error(t("lotoWorklow.messages.noPreviousDataFound"));
        return false;
      }

      const previousFormData = parentPreviousData?.["R4ZIDQWP"];
      const container2 = previousFormData?.container?.container2;
      const equipments = container2?.equipoSistemaAbscisaObjetoDelTrabajo || [];

      const equipmentFound = equipments.some(eq => eq.id?.toString() === qrResult.equipmentCode?.toString());

      if (!equipmentFound) {
        toast.error(t("lotoWorklow.messages.equipmentMismatchLoto"));
        return false;
      }

      return true;

    } catch (error) {
      toast.error(t("lotoWorklow.messages.errorValidatingQr"));
      return false;
    }
  };

  return (
    <Container open={open}>
      <Grid container 
        spacing={2} 
        alignItems="center"
        sx={{ mb: { xs: 2, md: 2 }, mt: { xs: 1, md: 0 } }}
      >
        <Grid item xs={12} md={4} xl={5}>
          <PageTitle>
            {t(`sidebar.${workflow.name}`)}
          </PageTitle>
        </Grid>
        <Grid item xs={12} md={6} lg={5} xl={5}>
          <SearchBar
            rows={data}
            setFilteredRequest={setFilteredRequest}
            filterText={t("lotoWorklow.search")}
            filterKeys={['code', 'e.workflowStatus?.title']}
          />
        </Grid>
      </Grid>

      <TableLoto
        headers={headers}
        filteredRequest={filteredRequest}
        rows={data}
        rowsCount={rowsCount}
        onRowClick={handleViewFormDetail}
        handleProcessForm={handleProcessForm}
        isList={true}
        loading={isLoading}
      />

      <BackdropLoading open={isLoading} />
      <ShowDialog
        openDialog={dialog.status}
        dialog={dialog}
        handleCloseDialog={() => setDialog({ status: false })}
      />

      <QrScannerModal
        open={openQrModal}
        onClose={handleCloseQrModal}
        rowName={selectedRow?.code ?? ""}
        startScanning={startScanning}
        onStartScanning={handleStartScanning}
        onScan={(result) => handleQrScan(result, handleScanSuccess, validateQrLotoList)}
        onError={handleQrError}
      />
    </Container>
  );
};